import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  FaComments, FaBell, FaPaperPlane, FaSearch, FaCircle,
  FaCheckCircle, FaExclamationTriangle, FaMoneyBillWave, FaBriefcase,
  FaStar, FaUserCircle, FaEllipsisV, FaCheckDouble,
} from "react-icons/fa";
import { getFullImageUrl } from "../../utils/getFullImageUrl";
import api from "../../api/axiosClient";

const norm = s => (s || "").toLowerCase().replace(/[_\s]/g, "");

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const secs = Math.floor((Date.now() - d) / 1000);
  if (secs < 60) return "now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d`;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

function alertVisual(type) {
  switch (norm(type)) {
    case "payment":
      return {
        icon: <FaMoneyBillWave />,
        bg: "rgba(255,240,0,.15)",
        color: "#000000",
      };

    case "job":
    case "newjob":
      return {
        icon: <FaBriefcase />,
        bg: "#dbeafe",
        color: "#3b82f6",
      };

    case "review":
    case "rating":
      return {
        icon: <FaStar />,
        bg: "rgba(255,240,0,.15)",
        color: "#000000",
      };

    case "warning":
    case "alert":
      return {
        icon: <FaExclamationTriangle />,
        bg: "#fee2e2",
        color: "#ef4444",
      };

    default:
      return {
        icon: <FaBell />,
        bg: "#f3f4f6",
        color: "#6b7280",
      };
  }
}

// GET /messages/conversations/ -> shape used by this tab.
const normalizeConversation = (c) => ({
  id: c.job_id ?? c.id,
  name: c.other_party_name || "Client",
  jobTitle: c.service || "",
  status: c.status,
  lastMessage: c.last_message || "",
  lastMessageAt: c.last_message_at,
  unread: Number(c.unread_count) || 0,
});

// The API sends `is_read`; older code used `read`.
const isAlertRead = (a) => Boolean(a.is_read ?? a.read);

function ConvoRow({ convo, active, onClick }) {
  const avatarUrl = getFullImageUrl(
    convo.avatar || convo.client?.profile_url
  );

  return (
    <div
      onClick={onClick}
      className={`msg-convo-row ${active ? "active" : ""}`}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="msg-avatar" />
      ) : (
        <div className="msg-avatar msg-avatar-fallback">
          <FaUserCircle size={22} />
        </div>
      )}

      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 6,
          }}
        >
          <p className="msg-convo-name">
            {convo.name || "Client"}
          </p>

          <span className="msg-convo-time">
            {timeAgo(convo.lastMessageAt)}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 6,
          }}
        >
          <p className="msg-convo-preview">
            {convo.lastMessage || "No messages yet"}
          </p>

          {convo.unread > 0 && (
            <span className="msg-unread-badge">
              {convo.unread}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageTab({ craftsman = {}, addToast }) {
  const [view, setView] = useState("chats");
  const [conversations, setConversations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);

  const scrollRef = useRef(null);

  const toast = useCallback(
    (msg, type = "info") => {
      if (addToast) addToast(msg, type);
    },
    [addToast]
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);

      try {
        const [convoRes, alertRes] = await Promise.allSettled([
          api.get("/messages/conversations/"),
          api.get("/craftsman/notifications/"),
        ]);

        if (!cancelled) {
          if (convoRes.status === "fulfilled") {
            const raw = Array.isArray(convoRes.value.data)
              ? convoRes.value.data
              : convoRes.value.data?.results || [];
            const list = raw.map(normalizeConversation);

            setConversations(list);

            if (list.length) {
              setActiveId(list[0].id);
            }
          }

          if (alertRes.status === "fulfilled") {
            const list = Array.isArray(alertRes.value.data)
              ? alertRes.value.data
              : alertRes.value.data?.results || [];

            setAlerts(list);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get(
          `/job-requests/${activeId}/messages/`

        );

        if (!cancelled) {
          setMessages(
            Array.isArray(data)
              ? data
              : data?.results || []
          );

          // Opening a thread marks the other person's messages as read.
          setConversations(prev =>
            prev.map(c =>
              c.id === activeId ? { ...c, unread: 0 } : c
            )
          );
          api
            .post(`/job-requests/${activeId}/messages/mark-read/`)
            .catch(() => {});
        }
      } catch {
        if (!cancelled) {
          setMessages([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop =
        scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const filteredConvos = useMemo(() => {
    if (!search.trim()) {
      return conversations;
    }

    const q = search.toLowerCase();

    return conversations.filter(
      c =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.lastMessage || "").toLowerCase().includes(q)
    );
  }, [conversations, search]);

  const unreadAlerts = alerts.filter(a => !isAlertRead(a)).length;

  const activeConvo = conversations.find(
    c => c.id === activeId
  );

  const sendMessage = async () => {
    if (!draft.trim() || !activeId) return;

    const text = draft.trim();

    setDraft("");
    setSending(true);

    const optimistic = {
      id: `local-${Date.now()}`,
      sender: "me",
      text,
      sent_at: new Date().toISOString(),
      pending: true,
    };

    setMessages(prev => [...prev, optimistic]);

    try {
      const { data } = await api.post(
        `/job-requests/${activeId}/messages/`,

        { text }
      );

      setMessages(prev =>
        prev.map(m =>
          m.id === optimistic.id ? data : m
        )
      );

      setConversations(prev =>
        prev.map(c =>
          c.id === activeId
            ? {
                ...c,
                lastMessage: text,
                lastMessageAt: new Date().toISOString(),
              }
            : c
        )
      );
    } catch {
      toast(
        "Message couldn't be sent — check your connection",
        "error"
      );

      setMessages(prev =>
        prev.map(m =>
          m.id === optimistic.id
            ? {
                ...m,
                failed: true,
                pending: false,
              }
            : m
        )
      );
    } finally {
      setSending(false);
    }
  };

  const markAlertRead = async id => {
    setAlerts(prev =>
      prev.map(a =>
        a.id === id
          ? { ...a, read: true, is_read: true }
          : a
      )
    );

    try {
      await api.post(
        `/craftsman/notifications/${id}/read/`
      );
    } catch {
      // Non-blocking
    }
  };

  const markAllRead = async () => {
    setAlerts(prev =>
      prev.map(a => ({
        ...a,
        read: true,
        is_read: true,
      }))
    );

    try {
      await api.post(
        "/craftsman/notifications/mark-all-read/"
      );
    } catch {
      // Non-blocking
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800;900&display=swap');

        .msg-wrap * {
          box-sizing: border-box;
        }

        .msg-wrap {
          font-family: 'Raleway', sans-serif;
          color: #000000;
        }

        .msg-tabs {
          display: flex;
          gap: .5rem;
          margin-bottom: 1.25rem;
        }

        .msg-tab-btn {
          border: 1px solid #e5e7eb;
          background: #ffffff;
          padding: .6rem 1.1rem;
          border-radius: 12px;
          font-family: 'Raleway', sans-serif;
          font-weight: 700;
          font-size: .85rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6b7280;
          transition: all .2s;
        }

        .msg-tab-btn.active {
          background: #fff000;
          color: #000000;
          border-color: #fff000;
        }

        .msg-tab-count {
          background: rgba(239,68,68,.15);
          color: #ef4444;
          font-size: .68rem;
          font-weight: 800;
          padding: 1px 7px;
          border-radius: 50px;
        }

        .msg-tab-btn.active .msg-tab-count {
          background: rgba(0,0,0,.12);
          color: #000000;
        }

        .msg-shell {
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 0;
          background: #ffffff;
          border: 1px solid #f0f0ef;
          border-radius: 18px;
          box-shadow: 0 2px 10px rgba(0,0,0,.03);
          overflow: hidden;
          min-height: 560px;
        }

        @media (max-width: 860px) {
          .msg-shell {
            grid-template-columns: 1fr;
          }
        }

        .msg-list-col {
          border-right: 1px solid #f5f5f4;
          display: flex;
          flex-direction: column;
          max-height: 640px;
        }

        .msg-search-wrap {
          padding: 1rem;
          border-bottom: 1px solid #f5f5f4;
          position: relative;
        }

        .msg-search-input {
          width: 100%;
          padding: 9px 12px 9px 34px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          font-family: 'Raleway', sans-serif;
          font-size: .85rem;
        }

        .msg-search-icon {
          position: absolute;
          left: 26px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .msg-convo-row {
          display: flex;
          gap: 10px;
          padding: .9rem 1rem;
          cursor: pointer;
          border-bottom: 1px solid #f8f8f7;
          transition: background .15s;
        }

        .msg-convo-row:hover {
          background: #f9fafb;
        }

        .msg-convo-row.active {
          background: rgba(255,240,0,.10);
          border-left: 3px solid #fff000;
        }

        .msg-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        }

        .msg-avatar-fallback {
          background: #f3f4f6;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .msg-convo-name {
          font-size: .85rem;
          font-weight: 700;
          margin: 0;
        }

        .msg-convo-time {
          font-size: .68rem;
          color: #9ca3af;
          flex-shrink: 0;
        }

        .msg-convo-preview {
          font-size: .78rem;
          color: #9ca3af;
          margin: 2px 0 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 190px;
        }

        .msg-unread-badge {
          background: #fff000;
          color: #000000;
          font-size: .65rem;
          font-weight: 800;
          border-radius: 50px;
          padding: 1px 6px;
          flex-shrink: 0;
        }

        .msg-thread-col {
          display: flex;
          flex-direction: column;
        }

        .msg-thread-head {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f5f5f4;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .msg-thread-body {
          flex: 1;
          overflow-y: auto;
          padding: 1.25rem 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .msg-bubble {
          max-width: 70%;
          padding: .65rem 1rem;
          border-radius: 16px;
          font-size: .85rem;
          line-height: 1.4;
          font-family: 'Raleway', sans-serif;
        }

        .msg-bubble.mine {
          align-self: flex-end;
          background: #fff000;
          color: #000000;
          border-bottom-right-radius: 4px;
        }

        .msg-bubble.theirs {
          align-self: flex-start;
          background: #f3f4f6;
          color: #000000;
          border-bottom-left-radius: 4px;
        }

        .msg-bubble-meta {
          font-size: .65rem;
          opacity: .65;
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .msg-compose {
          display: flex;
          gap: 8px;
          padding: 1rem 1.25rem;
          border-top: 1px solid #f5f5f4;
        }

        .msg-compose-input {
          flex: 1;
          padding: 11px 16px;
          border-radius: 50px;
          border: 1px solid #e5e7eb;
          font-family: 'Raleway', sans-serif;
          font-size: .88rem;
        }

        .msg-send-btn {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: none;
          background: #fff000;
          color: #000000;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
        }

        .msg-send-btn:disabled {
          opacity: .5;
          cursor: not-allowed;
        }

        .msg-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex: 1;
          color: #9ca3af;
          padding: 2rem;
          text-align: center;
        }

        .msg-alert-row {
          display: flex;
          gap: 12px;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f5f5f4;
          align-items: flex-start;
          cursor: pointer;
        }

        .msg-alert-row:hover {
          background: #f9fafb;
        }

        .msg-alert-row.unread {
          background: rgba(255,240,0,.06);
        }

        .msg-alert-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .msg-alert-title {
          font-size: .85rem;
          font-weight: 700;
          margin: 0;
          color: #000000;
        }

        .msg-alert-body {
          font-size: .8rem;
          color: #6b7280;
          margin: 2px 0 0;
        }

        .msg-alert-time {
          font-size: .7rem;
          color: #9ca3af;
          margin-top: 4px;
        }

        .msg-mark-all {
          border: 1px solid #e5e7eb;
          background: #ffffff;
          border-radius: 10px;
          padding: 6px 14px;
          font-family: 'Raleway', sans-serif;
          font-size: .78rem;
          font-weight: 700;
          color: #000000;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .msg-mark-all:hover {
          background: #fff000;
          border-color: #fff000;
        }
      `}</style>

      <div className="msg-wrap">
        <div className="msg-tabs">
          <button
            className={`msg-tab-btn ${
              view === "chats" ? "active" : ""
            }`}
            onClick={() => setView("chats")}
          >
            <FaComments />
            Chats
          </button>

          <button
            className={`msg-tab-btn ${
              view === "alerts" ? "active" : ""
            }`}
            onClick={() => setView("alerts")}
          >
            <FaBell />
            Alerts

            {unreadAlerts > 0 && (
              <span className="msg-tab-count">
                {unreadAlerts}
              </span>
            )}
          </button>
        </div>

        {view === "chats" ? (
          <div className="msg-shell">
            <div className="msg-list-col">
              <div className="msg-search-wrap">
                <FaSearch
                  className="msg-search-icon"
                  size={12}
                />

                <input
                  className="msg-search-input"
                  placeholder="Search conversations…"
                  value={search}
                  onChange={e =>
                    setSearch(e.target.value)
                  }
                />
              </div>

              <div
                style={{
                  overflowY: "auto",
                  flex: 1,
                }}
              >
                {loading ? (
                  <div
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "#9ca3af",
                      fontSize: ".85rem",
                    }}
                  >
                    Loading…
                  </div>
                ) : filteredConvos.length === 0 ? (
                  <div
                    style={{
                      padding: "2.5rem 1.5rem",
                      textAlign: "center",
                      color: "#9ca3af",
                    }}
                  >
                    <FaComments
                      size={30}
                      style={{
                        opacity: .15,
                        display: "block",
                        margin: "0 auto .75rem",
                      }}
                    />

                    <p
                      style={{
                        fontWeight: 700,
                        color: "#374151",
                        margin: 0,
                        fontSize: ".85rem",
                      }}
                    >
                      No conversations yet
                    </p>

                    <p
                      style={{
                        fontSize: ".78rem",
                        margin: ".25rem 0 0",
                      }}
                    >
                      Client messages about your jobs
                      appear here
                    </p>
                  </div>
                ) : (
                  filteredConvos.map(c => (
                    <ConvoRow
                      key={c.id}
                      convo={c}
                      active={c.id === activeId}
                      onClick={() =>
                        setActiveId(c.id)
                      }
                    />
                  ))
                )}
              </div>
            </div>

            <div className="msg-thread-col">
              {!activeConvo ? (
                <div className="msg-empty">
                  <FaComments
                    size={40}
                    style={{
                      opacity: .15,
                      marginBottom: 12,
                    }}
                  />

                  <p
                    style={{
                      fontWeight: 700,
                      color: "#374151",
                    }}
                  >
                    Select a conversation
                  </p>

                  <p style={{ fontSize: ".82rem" }}>
                    Pick a chat on the left to see messages
                  </p>
                </div>
              ) : (
                <>
                  <div className="msg-thread-head">
                    {getFullImageUrl(
                      activeConvo.avatar
                    ) ? (
                      <img
                        src={getFullImageUrl(
                          activeConvo.avatar
                        )}
                        alt=""
                        className="msg-avatar"
                      />
                    ) : (
                      <div className="msg-avatar msg-avatar-fallback">
                        <FaUserCircle size={22} />
                      </div>
                    )}

                    <div>
                      <p
                        style={{
                          fontWeight: 700,
                          fontSize: ".9rem",
                          margin: 0,
                        }}
                      >
                        {activeConvo.name || "Client"}
                      </p>

                      <p
                        style={{
                          fontSize: ".75rem",
                          color: "#9ca3af",
                          margin: 0,
                        }}
                      >
                        {activeConvo.jobTitle ||
                          "Direct message"}
                      </p>
                    </div>

                    <FaEllipsisV
                      style={{
                        marginLeft: "auto",
                        color: "#9ca3af",
                        cursor: "pointer",
                      }}
                    />
                  </div>

                  <div
                    className="msg-thread-body"
                    ref={scrollRef}
                  >
                    {messages.length === 0 ? (
                      <div
                        className="msg-empty"
                        style={{
                          flex: "unset",
                          padding: "2rem",
                        }}
                      >
                        <p style={{ fontSize: ".82rem" }}>
                          No messages yet — say hello 👋
                        </p>
                      </div>
                    ) : (
                      messages.map(m => (
                        <div
                          key={m.id}
                          className={`msg-bubble ${
                            m.sender === "me" ||
                            m.is_mine
                              ? "mine"
                              : "theirs"
                          }`}
                        >
                          {m.text}

                          <div className="msg-bubble-meta">
                            {timeAgo(
                              m.sent_at ||
                                m.created_at
                            )}

                            {(m.sender === "me" ||
                              m.is_mine) &&
                              !m.pending &&
                              !m.failed && (
                                <FaCheckDouble size={9} />
                              )}

                            {m.pending && (
                              <span>Sending…</span>
                            )}

                            {m.failed && (
                              <span
                                style={{
                                  color: "#ef4444",
                                }}
                              >
                                Failed
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="msg-compose">
                    <input
                      className="msg-compose-input"
                      placeholder="Type a message…"
                      value={draft}
                      onChange={e =>
                        setDraft(e.target.value)
                      }
                      onKeyDown={e => {
                        if (
                          e.key === "Enter" &&
                          !e.shiftKey
                        ) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />

                    <button
                      className="msg-send-btn"
                      onClick={sendMessage}
                      disabled={
                        sending || !draft.trim()
                      }
                    >
                      <FaPaperPlane size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div
            className="msg-shell"
            style={{
              gridTemplateColumns: "1fr",
            }}
          >
            <div>
              <div
                className="msg-thread-head"
                style={{
                  justifyContent: "space-between",
                }}
              >
                <div
                  className="msg-panel-title"
                  style={{
                    fontWeight: 800,
                    fontSize: ".9rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaBell size={12} />
                  Notifications
                </div>

                {unreadAlerts > 0 && (
                  <button
                    className="msg-mark-all"
                    onClick={markAllRead}
                  >
                    <FaCheckCircle size={11} />
                    Mark all read
                  </button>
                )}
              </div>

              {loading ? (
                <div
                  style={{
                    padding: "2rem",
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: ".85rem",
                  }}
                >
                  Loading…
                </div>
              ) : alerts.length === 0 ? (
                <div
                  style={{
                    padding: "3rem 1.5rem",
                    textAlign: "center",
                    color: "#9ca3af",
                  }}
                >
                  <FaBell
                    size={34}
                    style={{
                      opacity: .15,
                      display: "block",
                      margin: "0 auto 1rem",
                    }}
                  />

                  <p
                    style={{
                      fontWeight: 700,
                      color: "#374151",
                      margin: 0,
                    }}
                  >
                    You're all caught up
                  </p>

                  <p
                    style={{
                      fontSize: ".85rem",
                      margin: ".25rem 0 0",
                    }}
                  >
                    Job, payment, and review alerts will
                    show up here
                  </p>
                </div>
              ) : (
                alerts.map(a => {
                  const v = alertVisual(a.type);

                  return (
                    <div
                      key={a.id}
                      className={`msg-alert-row ${
                        !isAlertRead(a) ? "unread" : ""
                      }`}
                      onClick={() =>
                        markAlertRead(a.id)
                      }
                    >
                      <div
                        className="msg-alert-icon"
                        style={{
                          background: v.bg,
                          color: v.color,
                        }}
                      >
                        {v.icon}
                      </div>

                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <p className="msg-alert-title">
                          {a.title || a.message}
                        </p>

                        {a.body && (
                          <p className="msg-alert-body">
                            {a.body}
                          </p>
                        )}

                        <p className="msg-alert-time">
                          {timeAgo(a.created_at)}
                        </p>
                      </div>

                      {!isAlertRead(a) && (
                        <FaCircle
                          size={7}
                          color="#fff000"
                          style={{
                            marginTop: 6,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

MessageTab.propTypes = {
  craftsman: PropTypes.object,
  addToast: PropTypes.func,
};

export default MessageTab;